import { ArrowRight, TrendingUp, Globe2, Database, Users, CheckCircle2, FileText, Leaf, Network, BarChart3, Lightbulb, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Globe3D from '../components/Globe3D';

const Home = () => {
  // Intelligence Pillars Data
  const intelligencePillars = [
    {
      icon: <Network className="w-8 h-8 text-[#007BFF]" />,
      title: 'Connected Insights',
      description: 'Enerlligence integrates intelligence across the global energy ecosystem—linking commodity markets, infrastructure development, supply chains, and emissions pathways into a unified view.'
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-[#007BFF]" />,
      title: 'Deeper Market Understanding',
      description: 'Through structured analysis of countries, regions, companies, and energy markets, Enerlligence provides insights into supply and demand dynamics, policy developments, infrastructure expansion, and investment opportunities.'
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-[#007BFF]" />,
      title: 'Intelligence for the Future',
      description: 'Advanced analytics, modelling frameworks, and strategic research help organizations anticipate market shifts and navigate the evolving global energy transition.'
    }
  ];

  // Coverage Items
  const coverageItems = [
    'Commodity Prices',
    'Crude Oil',
    'Natural Gas',
    'Coal',
    'Solar Energy',
    'Wind Energy',
    'Hydropower',
    'Nuclear Energy',
    'Bioenergy',
    'Geothermal Energy',
    'Critical Minerals',
    'Emissions Tracking'
  ];

  // Insights Data
  const insightArticles = [
    {
      category: 'Energy Markets',
      title: 'Global LNG Supply Expansion: Investment Opportunities and Infrastructure Risks',
      date: '5 March 2026',
      readTime: '3 min read'
    },
    {
      category: 'Energy Transition',
      title: 'Critical Minerals and the Race to Secure Energy Transition Supply Chains',
      date: '7 March 2026',
      readTime: '2 min read'
    },
    {
      category: 'Geopolitics',
      title: 'Geopolitical Tensions and the Reshaping of Global Energy Trade',
      date: '6 March 2026',
      readTime: '2 min read'
    },
    {
      category: 'Commodity Intelligence',
      title: 'Crude Oil Market Outlook: Supply Discipline Meets Demand Uncertainty',
      date: '4 March 2026',
      readTime: '2 min read'
    },
    {
      category: 'Regional Energy Analysis',
      title: "Africa's Emerging Role in Global Energy Supply and Energy Transition",
      date: '3 March 2026',
      readTime: '3 min read'
    },
    {
      category: 'Emissions Tracking',
      title: 'Tracking Global Carbon Emissions Across the Energy Transition',
      date: '2 March 2026',
      readTime: '2 min read'
    }
  ];

  const services = [
    {
      icon: <Database className="w-8 h-8 text-[#1F3C88]" />,
      title: 'Commodity Research',
      description: 'Comprehensive coverage of oil, gas, coal, power, and critical minerals with supply-demand outlooks',
      link: '/energia-iq'
    },
    {
      icon: <Globe2 className="w-8 h-8 text-[#1F3C88]" />,
      title: 'Country Analysis',
      description: 'In-depth country risk assessments, regulatory analysis, and geopolitical intelligence across key energy markets',
      link: '/energia-iq'
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-[#1F3C88]" />,
      title: 'Market Intelligence',
      description: 'Real-time data and analytics on global energy and mining markets with proprietary forecasting models',
      link: '/insights'
    },
    {
      icon: <Leaf className="w-8 h-8 text-[#1F3C88]" />,
      title: 'Emissions Tracking',
      description: 'Comprehensive monitoring and analysis of energy sector emissions, carbon markets, and climate policy impacts',
      link: '/energia-iq'
    },
    {
      icon: <FileText className="w-8 h-8 text-[#1F3C88]" />,
      title: 'Specialized Reports',
      description: 'Board-ready analytical reports supporting strategic planning, valuation analysis, and capital deployment decisions',
      link: '/reports'
    }
  ];

  const differentiators = [
    'Independent, unbiased analysis free from vendor or producer influence',
    'Proprietary data collection methodologies across 50+ energy markets',
    'Expert analyst team with decades of industry and consulting experience',
    'Customized intelligence solutions tailored to client decision-making needs',
    'Real-time monitoring and rapid response to market-moving events'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with 3D Globe */}
      <section className="relative min-h-[90vh] flex items-end pb-16 overflow-hidden">
        {/* Deep navy to black gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0d1f3c] to-[#060d18]"></div>
        
        {/* 3D Globe Background */}
        <Globe3D />
        
        {/* Subtle overlay gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-[#0a1628]/30 pointer-events-none"></div>
        
        {/* Centered Text Content - Positioned Lower */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
          <div className="w-full flex flex-col items-center justify-center">
            {/* Badge */}
            <div className="inline-block px-5 py-2.5 bg-[#1F3C88]/60 backdrop-blur-sm text-white text-sm font-medium rounded-full mb-8 border border-white/10">
              Independent Global Energy Intelligence
            </div>
            
            {/* Primary Headline - Perfectly Centered */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-white mb-6 leading-tight tracking-tight md:whitespace-nowrap text-center">
              Connected Insights, Forward Thinking
            </h1>
            
            {/* Supporting Paragraph - Pure White */}
            <p className="text-base sm:text-lg md:text-xl text-white mb-12 leading-relaxed max-w-3xl text-center">
              Enerlligence Analytics is a global energy intelligence platform providing proprietary data, market research, and strategic insights across energy, natural resources, and the energy transition to support informed investment and policy decisions.
            </p>
            
            {/* Call-to-Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
              <Link
                to="/energia-iq"
                data-testid="hero-cta-empower"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#8B0000] text-white font-semibold rounded-md hover:bg-[#6B0000] transition-all duration-200 shadow-lg hover:shadow-xl min-w-[240px]"
              >
                Empower Better Decisions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/insights"
                data-testid="hero-cta-strategic"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#CFA300] text-white font-semibold rounded-md hover:bg-[#B8920A] transition-all duration-200 shadow-lg hover:shadow-xl min-w-[240px]"
              >
                Explore Strategic Insights
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/reports"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#007BFF] text-white font-semibold rounded-md hover:bg-[#0066DD] transition-all duration-200 shadow-lg hover:shadow-xl min-w-[240px]"
              >
                Browse Reports
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence Platform Section */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50" data-testid="connected-insight-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Description */}
          <div className="text-center mb-16">
            <p className="text-base lg:text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Enerlligence Analytics connects data, expertise, and advanced analytics to deliver intelligence across the global energy system. By integrating insights across commodities, countries, markets, and the energy transition, Enerlligence enables governments, investors, and industry leaders to anticipate change, understand complex markets, and make confident strategic decisions.
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-20">
            {/* LEFT SIDE - Intelligence Pillars */}
            <div className="space-y-6">
              {intelligencePillars.map((pillar, index) => (
                <div 
                  key={index} 
                  className="group p-6 bg-[#F5F5F5] rounded-xl shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#007BFF]/30 transition-all duration-300"
                  data-testid={`pillar-${index + 1}`}
                >
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:bg-[#007BFF]/10 transition-colors duration-300">
                      {pillar.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#0F2A4D] mb-2">
                        {pillar.title}
                      </h3>
                      <p className="text-base text-slate-600 leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* RIGHT SIDE - Visual Representation */}
            <div className="relative lg:sticky lg:top-8">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/028e750a-ca93-48a5-b1f7-6939a19334dc/images/68cd1be6954fc9cb048aad691a86acd03e5a42f64ef71af6fd276e5fe72fefdc.png"
                  alt="Global Energy Intelligence Analytics Platform"
                  className="w-full h-auto object-cover"
                  data-testid="analytics-visual"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A4D]/20 via-transparent to-transparent pointer-events-none"></div>
              </div>
              <p className="text-center text-sm text-slate-500 mt-4 italic">
                Unified global energy intelligence platform
              </p>
            </div>
          </div>

          {/* Coverage List */}
          <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm border border-slate-100 mb-12">
            <h3 className="text-2xl font-bold text-[#0F2A4D] mb-8 text-center">
              Enerlligence Intelligence Coverage
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {coverageItems.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 p-3 bg-[#F5F5F5] rounded-lg hover:bg-[#007BFF]/10 transition-colors duration-200"
                  data-testid={`coverage-item-${index}`}
                >
                  <div className="w-2 h-2 bg-[#007BFF] rounded-full flex-shrink-0"></div>
                  <span className="text-sm font-medium text-[#0F2A4D]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Closing Statement */}
          <div className="text-center">
            <div className="inline-block px-8 py-4 bg-gradient-to-r from-[#0F2A4D] to-[#1F3C88] rounded-lg">
              <p className="text-base text-white font-medium max-w-3xl">
                Energia IQ delivers connected insight across the global energy system—empowering forward-thinking decisions in a rapidly evolving energy landscape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Intelligence Services
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Comprehensive intelligence solutions designed for energy sector decision-makers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-[#F8F9FA] p-6 rounded-lg border border-slate-200 hover:border-[#007BFF] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-lg font-semibold text-[#0F2A4D] mb-2">
                  {service.title}
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Insights & Thought Leadership Section */}
      <section className="py-20 bg-slate-50" data-testid="insights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Insights & Thought Leadership
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Enerlligence publishes expert analysis, market intelligence, and strategic commentary on global energy markets, commodities, and the evolving energy transition. By delivering timely research across geopolitics, supply chains, and market dynamics, our insights help governments, investors, and industry leaders understand complex developments, identify emerging opportunities, and navigate the forces shaping the global energy system.
            </p>
          </div>

          {/* Featured Insight Banner */}
          <div className="bg-[#0F2A4D] rounded-xl p-8 lg:p-10 mb-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-white/5 to-transparent pointer-events-none"></div>
            <div className="relative z-10">
              <span className="inline-block px-3 py-1 bg-white/10 text-white text-xs font-semibold uppercase tracking-wider rounded mb-4 border border-white/20">
                Featured Insight
              </span>
              <h3 className="text-xl lg:text-2xl font-bold text-white mb-4 max-w-3xl">
                Global Energy Markets Outlook: Strategic Intelligence for Energy Leaders
              </h3>
              <p className="text-base text-white/90 mb-6 max-w-2xl leading-relaxed">
                As geopolitical developments, commodity markets, and the global energy transition reshape the energy landscape, decision-makers require deeper intelligence to navigate uncertainty and identify emerging opportunities.
              </p>
              <Link
                to="/insights"
                className="inline-flex items-center text-white font-semibold hover:text-white/80 transition-colors duration-200 group"
              >
                Explore the analysis
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Insights Article Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {insightArticles.map((article, index) => (
              <Link
                key={index}
                to="/insights"
                className="bg-white rounded-lg border border-slate-200 p-6 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 group"
                data-testid={`insight-card-${index}`}
              >
                <span className="inline-block px-2.5 py-1 bg-slate-100 text-[#0F2A4D] text-xs font-medium rounded mb-3">
                  {article.category}
                </span>
                <h4 className="text-lg font-semibold text-[#0F2A4D] mb-4 leading-snug group-hover:text-[#007BFF] transition-colors">
                  {article.title}
                </h4>
                <div className="flex items-center text-sm text-slate-500 gap-3">
                  <span>{article.date}</span>
                  <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {article.readTime}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Section Footer */}
          <div className="text-center">
            <p className="text-base text-slate-600 mb-6 max-w-2xl mx-auto">
              Enerlligence Insights delivers <span className="font-semibold text-[#0F2A4D]">connected intelligence and forward-looking analysis</span> across global energy markets, commodities, and the energy transition.
            </p>
            <Link
              to="/insights"
              className="inline-flex items-center text-[#007BFF] font-semibold hover:text-[#0F2A4D] transition-colors duration-200 group"
            >
              Explore All Insights
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Industry Leaders Trust Enerlligence */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-6">
                Why Industry Leaders Trust Enerlligence
              </h2>
              <p className="text-base lg:text-lg text-slate-600 mb-8">
                Our commitment to independence, analytical rigor, and client service has made us a trusted partner to Fortune 500 energy companies, sovereign wealth funds, and government agencies worldwide.
              </p>
              <div className="space-y-4">
                {differentiators.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#007BFF] flex-shrink-0 mt-0.5" />
                    <p className="text-base text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/24244231/pexels-photo-24244231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="LNG Terminal Infrastructure"
                className="rounded-xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Schedule a Consultation */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-14 h-14 text-white/80 mx-auto mb-6" />
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Join Leading Organizations Worldwide
          </h2>
          <p className="text-base lg:text-lg text-blue-100 mb-8">
            Access the intelligence that drives strategic decisions in global energy markets
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#1D3B87] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
